import axios from 'axios';
import secrets from './secrets';
import {
  clearAuthSession,
  getAccessToken,
  getRefreshToken,
  getTokenType,
  isGuestMode,
  saveAuthSession,
} from '../auth/authStorage';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ?? 'http://192.168.24.208:8090/public';

const httpClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 10000,
});

let refreshPromise = null;

async function refreshTokens() {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('Brak refresh tokenu');
  }

  if (!secrets.clientId || !secrets.clientSecret) {
    throw new Error('Brak client_id lub client_secret w src/api/secrets.js');
  }

  refreshPromise = axios
    .post(
      `${API_BASE_URL}/api/v2/refresh-token`,
      {
        refresh_token: refreshToken,
        client_id: secrets.clientId,
        client_secret: secrets.clientSecret,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        timeout: 10000,
      },
    )
    .then((response) => {
      const envelope = response.data;
      if (envelope?.error) {
        const err = new Error(
          envelope.error?.message ?? 'Odświeżanie tokenu nie powiodło się',
        );
        err.response = response;
        throw err;
      }
      if (!envelope?.access_token) {
        const err = new Error('Brak access_token w odpowiedzi serwera');
        err.response = response;
        throw err;
      }
      saveAuthSession(envelope);
      return envelope;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

httpClient.interceptors.request.use((config) => {
  const url = config.url ?? '';
  const skipAccessToken =
    url.includes('/api/v2/login') || url.includes('/api/v2/refresh-token');
  if (!skipAccessToken) {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `${getTokenType()} ${token}`;
    }
  }
  return config;
});

httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const statusCode = error?.response?.status;
    const url = originalRequest?.url ?? '';
    const skipRefreshRetry =
      url.includes('/api/v2/login') || url.includes('/api/v2/refresh-token');

    if (statusCode !== 401 || originalRequest?._retry || skipRefreshRetry) {
      return Promise.reject(error);
    }

    try {
      originalRequest._retry = true;
      await refreshTokens();
      console.info('[auth] Sesja odświeżona po 401; ponawiam żądanie.', {
        url: originalRequest?.url,
      });
      originalRequest.headers.Authorization = `${getTokenType()} ${getAccessToken()}`;
      return httpClient(originalRequest);
    } catch (refreshError) {
      const wasGuest = isGuestMode();
      clearAuthSession();
      if (!wasGuest) {
        const refreshStatus = refreshError?.response?.status;
        const refreshData = refreshError?.response?.data;
        console.warn(
          '[auth] Wylogowanie: odświeżenie sesji nie powiodło się (401 → refresh). Przekierowanie na /login.',
          {
            message: refreshError?.message,
            refreshHttpStatus: refreshStatus,
            refreshResponse: refreshData,
            failedRequestUrl: originalRequest?.url,
          },
        );
        if (typeof window !== 'undefined') {
          window.location.assign('/login');
        }
      }
      return Promise.reject(refreshError);
    }
  },
);

export function createCrudResource(resourcePath) {
  return {
    list: (params) => httpClient.get(resourcePath, { params }),
    get: (id, params) => httpClient.get(`${resourcePath}/${id}`, { params }),
    create: (payload) => httpClient.post(resourcePath, payload),
    update: (id, payload) => httpClient.patch(`${resourcePath}/${id}`, payload),
    replace: (id, payload) => httpClient.put(`${resourcePath}/${id}`, payload),
    remove: (id) => httpClient.delete(`${resourcePath}/${id}`),
  };
}

export default httpClient;
